import {lineIntersection, relativeToImagePlane, triangleOrthoCenter} from "./geometry"
import {Vector3, Matrix4} from "three";

// could be configured from somewhere
let controlPoints = {
    // 'origin': {'x': 0.4955782999959623, 'y': 0.5148031212070425},
    'origin': {'x': 0.5, 'y': 0.5},
    'principalPoint': {'x': 0, 'y': 0}
}
let DEFAULT_CAMERA_DISTANCE_SCALE = 10;

export const solve2vp = function (jsonInput) {
    let lines = [jsonInput.controlPointsStateBase.firstVanishingPoint,
        jsonInput.controlPointsState2VP.secondVanishingPoint,
        jsonInput.controlPointsState2VP.thirdVanishingPoint];
    controlPoints.origin.x = lines[2].lineSegments[0][0].x;
    controlPoints.origin.y = lines[2].lineSegments[0][0].y;
    controlPoints.principalPoint = jsonInput.controlPointsStateBase.principalPoint;
    let inputVanishingPoints = findVanishingPoints([lines[0], lines[1]],
        jsonInput.imageSize.width,
        jsonInput.imageSize.height);
    // TODO different modes for principalPoints
    // console.log("jsonInput", jsonInput);
    let principalPoint = relativeToImagePlane(controlPoints.principalPoint,
        jsonInput.imageSize.width,
        jsonInput.imageSize.height);
    switch (jsonInput.calibrationSettings2VP.principalPointMode) {
        case "FromThirdVanishingPoint":
            let thirdVanishingPointArray = findVanishingPoints([lines[2]],
                jsonInput.imageSize.width,
                jsonInput.imageSize.height);
            if (thirdVanishingPointArray) {
                let thirdVanishingPoint = thirdVanishingPointArray[0];
                principalPoint = triangleOrthoCenter(
                    inputVanishingPoints[0], inputVanishingPoints[1], thirdVanishingPoint
                );
            }
            break
    }
    let focalLengthRelative = findFocalLength(
        inputVanishingPoints[0], inputVanishingPoints[1], principalPoint
    );
    let result = computeCameraParameters(inputVanishingPoints, principalPoint, focalLengthRelative,
        jsonInput.imageSize.width, jsonInput.imageSize.height, {});
    return result;
}

const computeCameraParameters = function (vanishingPoints,
                                          principalPoint,
                                          focalLengthRelative,
                                          width,
                                          height,
                                          settings) {
    let axisMatrix = new Matrix4();
    axisMatrix.set( 1, 0, 0, 0,
        0, 1, 0, 0,
        0, 0, 1, 0,
        0, 0, 0, 1);
    let cameraParameters = {};
    let horizontalFieldOfView = computeFieldOfView(
        width,
        height,
        focalLengthRelative,
        false
    );
    cameraParameters.horizontalFieldOfView = horizontalFieldOfView;
    let verticalFieldOfView = computeFieldOfView(
        width,
        height,
        focalLengthRelative,
        true
    )
    cameraParameters.verticalFieldOfView = verticalFieldOfView;
    cameraParameters.cameraRotationMatrix = computeCameraRotationMatrix(
        vanishingPoints[0], vanishingPoints[1], focalLengthRelative, principalPoint
    );
    cameraParameters.viewTransform = axisMatrix.premultiply(cameraParameters.cameraRotationMatrix);
    computeTranslationVector(
        controlPoints,
        // settings,
        width,
        height,
        cameraParameters
    )

    cameraParameters.cameraTransform = new Matrix4();
    cameraParameters.cameraTransform.getInverse(cameraParameters.viewTransform);
    changeCoordinateSystem(cameraParameters.cameraTransform);
    return cameraParameters;
}

let changeCoordinateSystem = function (cameraTransform) {
    let e = new Matrix4();
    e.set(1, 0, 0, 0,
        0, 0, 1, 0,
        0, -1, 0, 0,
        0, 0, 0, 1);
    cameraTransform.premultiply(e);
}

let computeTranslationVector = function (controlPoints, width, height, cameraParameters) {
    let origin = relativeToImagePlane(controlPoints.origin, width, height);
    let principal = relativeToImagePlane(controlPoints.principalPoint, width, height);
    let k = Math.tan(0.5 * cameraParameters.horizontalFieldOfView);
    let origin3D = new Vector3(
        k * (origin.x - principal.x),
        k * (origin.y - principal.y),
        -1
    ).multiplyScalar(DEFAULT_CAMERA_DISTANCE_SCALE);
    cameraParameters.viewTransform.elements[12] = origin3D.x;
    cameraParameters.viewTransform.elements[13] = origin3D.y;
    cameraParameters.viewTransform.elements[14] = origin3D.z;
}

const computeCameraRotationMatrix = function (Fu, Fv, focalLengthRelative, principalPoint) {
    let OFu = new Vector3(Fu.x - principalPoint.x, Fu.y - principalPoint.y, -focalLengthRelative);
    let OFv = new Vector3(Fv.x - principalPoint.x, Fv.y - principalPoint.y, -focalLengthRelative);
    let s1 = OFu.length();
    let upRc = OFu.clone();
    upRc.normalize();
    let s2 = OFv.length();
    let vpRc = OFv.clone();
    vpRc.normalize();

    let wpRc = upRc.clone();
    wpRc.cross(vpRc);
    let M = new Matrix4();
    M.set(
        OFu.x / s1, OFv.x / s2, wpRc.x, 0,
        OFu.y / s1, OFv.y / s2, wpRc.y, 0,
        -focalLengthRelative / s1, -focalLengthRelative / s2, wpRc.z, 0,
        0, 0, 0, 1);
    return M;
}

const computeFieldOfView = function (width, height, focal, vertical) {
    let aspectRatio = width / height
    let d = vertical ? 1 / aspectRatio : 1
    return 2 * Math.atan(d / focal)
}

const findFocalLength = function (vp1, vp2, principalPoint) {
    let dirFuFv = new Vector3(vp1.x - vp2.x, vp1.y - vp2.y).normalize();
    let FvP = new Vector3(principalPoint.x - vp2.x, principalPoint.y - vp2.y);
    let proj = dirFuFv.dot(FvP);
    let Puv = {
        x: proj * dirFuFv.x + vp2.x,
        y: proj * dirFuFv.y + vp2.y
    };

    let PPuv = new Vector3(principalPoint.x - Puv.x, principalPoint.y - Puv.y).length();
    let FvPuv = new Vector3(vp2.x - Puv.x, vp2.y - Puv.y).length();
    let FuPuv = new Vector3(vp1.x - Puv.x, vp1.y - Puv.y).length();

    let fSq = FvPuv * FuPuv - PPuv * PPuv;
    if (fSq <= 0) {
        return null;
    }

    return Math.sqrt(fSq);
}

const findVanishingPoints = function (lines, width, height) {
    let result = [];
    let errors = [];
    for (let i = 0; i < lines.length; i++) {
        let vanishingPoint = lineIntersection(lines[i].lineSegments[0], lines[i].lineSegments[1]);
        if (vanishingPoint) {
            // TODO imagePlaneToRelative move to some kind of converter
            result.push(relativeToImagePlane(vanishingPoint, width, height));
        } else {
            errors.push('Failed to compute vanishing point');
        }
    }
    return errors.length == 0 ? result : null;
}

const unitVectors = {
    "xNegative": new Vector3(-1, 0, 0),
    "xPositive": new Vector3(1, 0, 0),
    "yNegative": new Vector3(0, -1, 0),
    "yPositive": new Vector3(0, 1, 0),
    "zNegative": new Vector3(0, 0, -1),
    "zPositive": new Vector3(0, 0, 1)
}
